import { FormikProps } from 'formik';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Banner from '@cpg-its/ads.containers.banner';
import { FormField } from '../../../global/components/form-element/form-field';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { useParentRoute } from '../../../global/hooks/useParentRoute';
import { DemographicsContext } from '../../../global/models/demographics-model';
import { TcsmEpiApiState, TcsmStates } from '../../../root/tcsm-model';
import { EmailsDisplay } from '../../account-info/components/read-only/emails-display';
import { MailingAddressDisplay } from '../../account-info/components/read-only/mailing-address-display';
import { MaritalStatusDisplay } from '../../account-info/components/read-only/marital-status-display';
import { PersonFieldsContentDisplay } from '../../account-info/components/read-only/person-fields-display';
import { PhonesDisplay } from '../../account-info/components/read-only/phones-display';
import { AccountInfoRoutes } from '../../account-info/constants/routes';
import { PersonalInfoState } from '../../account-info/features/personal-info/personal-info-models';
import { RelationshipsState } from '../../account-info/features/relationships/relationships-model';
import { getCurrentSpouse } from '../../account-info/features/relationships/relationships-selectors';
import { getInitialEmails, getInitialMailingAddress, getInitialPersonInfo, getInitialPhones } from '../../account-info/helpers/form-init-helpers';
import { isRetiree } from '../helpers/annual-enrollment-helpers';
import { ShouldShowKaiserArbitration } from '../../../global/helpers/kaiser';
import { AnnualEnrollmentFormValues } from '../models/annual-enrollment-form-models';
import { AnnualEnrollmentState } from '../models/annual-enrollment-models';
import { AnnualEnrollmentSummary } from './annual-enrollment-summary';
import { AnnualEnrollmentCommentsDisplay } from './read-only/annual-enrollment-comments-display';
import { AnnualEnrollmentDependentsDisplay } from './read-only/annual-enrollment-dependents-display';
import { AnnualEnrollmentParticipantsDisplay } from './read-only/annual-enrollment-participants-display';
import { AnnualEnrollmentPlansDisplay } from './read-only/annual-enrollment-plans-display';
import { isNotApplicableRate } from '../../../global/helpers/format-helpers';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';

interface Props {
	formProps: FormikProps<AnnualEnrollmentFormValues>;
	previousAction: () => void;
	medicalExpired: boolean,
	dentalExpired: boolean,
}
export function AnnualEnrollmentReview(props: Props) {

	const { formProps, previousAction, medicalExpired, dentalExpired } = props;

	const app = useContext(DemographicsContext);
	const personState = useSelector<TcsmStates, PersonalInfoState>(state => state.personalInfo[app]);
	const relationshipsState = useSelector<TcsmStates, RelationshipsState>(state => state.relationships[app]);
	const AEState = useSelector<TcsmStates, AnnualEnrollmentState>(state => state.annualEnrollment[app]);

	const epiState = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const labelSettings = epiState?.labelSettingState?.epiContent;
	const AEProps = epiState?.annualEnrollmentSettingsState?.epiContent;

	const parentRoute = useParentRoute();

	const personalInfoLink = `${parentRoute}${AccountInfoRoutes.PersonalInfoSegment}`;
	const maritalStatusLink = `${parentRoute}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`;
	const dependentsLink = `${parentRoute}${AccountInfoRoutes.RelationshipsSegment}`;

	const agreementLabel = isRetiree(AEState.AEInfo) && AEProps?.pensionDeductionAgreementLabel
		? AEProps.pensionDeductionAgreementLabel
		: <WysiwygWithReactLink className='rich-text--full' content={AEProps?.agreementLabel} />;
	const medicalRate = AEState.AEInfo?.offerings?.M?.plans?.filter(x => x.code === formProps?.values?.medicalPlan)[0]?.rates_by_tier[AEState.tierCalculations?.M?.display_tier] || 0;
	const dentalRate = AEState.AEInfo?.offerings?.D?.plans?.filter(x => x.code === formProps?.values?.dentalPlan)[0]?.rates_by_tier[AEState.tierCalculations?.D?.display_tier] || 0;

	const showNotApplicableRate = (formProps?.values?.medicalPlan && isNotApplicableRate(medicalRate)) || (formProps?.values?.dentalPlan && isNotApplicableRate(dentalRate));

	const renderEditLink = (path: string) => {
		return (
			<Link to={path}>{AEProps.editLabel}</Link>
		);
	};

	const buttonClasses = buttonStyles();
	const renderEditButton = () => {
		return (
			<button className={buttonClasses.linkButton} type='button' onClick={() => previousAction()}>{AEProps?.editLabel}</button>
		);
	};

	const currentPerson = personState?.currentPerson;

	const renderPensionDeductionAgreementWarning = () => {
		if (AEProps?.pensionDeductionAgreementWarning && isRetiree(AEState.AEInfo)) {
			return (
				<DividerSection removeDivider>
					<Banner banner={{
						type: 'warning',
						label: <WysiwygWithReactLink content={AEProps?.pensionDeductionAgreementWarning} />,
					}} />
				</DividerSection>
			);
		}

		return null;
	};

	return (
		<>
			<div className='grid-main-content'>
				<DividerSection removeDivider sectionText={AEProps?.personalInformationHeader} secondarySectionNode={renderEditLink(personalInfoLink)}>
					<PersonFieldsContentDisplay
						labelSetting={labelSettings}
						values={getInitialPersonInfo(currentPerson)}
						dobMasked
					/>
				</DividerSection>
				<MailingAddressDisplay
					labelSetting={labelSettings}
					values={getInitialMailingAddress(currentPerson)}
				/>
				<DividerSection removeDivider>
					<PhonesDisplay
						labelSetting={labelSettings}
						values={getInitialPhones(currentPerson)}
					/>
				</DividerSection>
				<DividerSection>
					<EmailsDisplay
						labelSetting={labelSettings}
						values={getInitialEmails(currentPerson)}
					/>
				</DividerSection>

				<MaritalStatusDisplay
					labelSetting={labelSettings}
					annualEnrollmentProps={AEProps}
					person={personState?.currentPerson}
					spouse={getCurrentSpouse(relationshipsState?.spouses)}
					editLink={renderEditLink(maritalStatusLink)}
				/>

				<AnnualEnrollmentDependentsDisplay
					annualEnrollmentProps={AEProps}
					dependents={AEState.AEInfo?.eligible_dependents}
					editLink={renderEditLink(dependentsLink)}
				/>

				<AnnualEnrollmentParticipantsDisplay
					annualEnrollmentProps={AEProps}
					aeInfo={AEState?.AEInfo}
					initialValues={formProps.initialValues}
					medicalParticipants={formProps.values.M}
					dentalParticipants={formProps.values.D}
					editButton={renderEditButton()}
					medicalExpired={medicalExpired}
					dentalExpired={dentalExpired}
				/>

				<AnnualEnrollmentPlansDisplay
					annualEnrollmentProps={AEProps}
					aeState={AEState}
					medicalPlan={formProps.values.medicalPlan}
					dentalPlan={formProps.values.dentalPlan}
					editButton={renderEditButton()}
					medicalExpired={medicalExpired}
					dentalExpired={dentalExpired}
				/>

				<AnnualEnrollmentSummary
					AEProps={AEProps}
					AEState={AEState}
					selectedDentalPlan={formProps.values.dentalPlan}
					selectedMedicalPlan={formProps.values.medicalPlan}
					showNotApplicable={showNotApplicableRate}
				/>

				<AnnualEnrollmentCommentsDisplay
					annualEnrollmentProps={AEProps}
					myNotes={formProps.values.memo}
					specialRequests={formProps.values.specialRequests}
					editButton={renderEditButton()}
				/>
				{
					renderPensionDeductionAgreementWarning()
				}
				<ContentSpacer>
					<FormField
						name='agreement'
						label={agreementLabel}
						component='checkBox'
						touched={formProps.touched}
						errors={formProps.errors}
						value={formProps?.values?.agreement ?? false}
						checked={formProps?.values?.agreement ?? false}
					/>
				</ContentSpacer>
				<ContentSpacer>
				 {ShouldShowKaiserArbitration({
					 plans: AEState?.AEInfo?.offerings?.M?.plans,
					 code: formProps?.values?.medicalPlan,
					 personZip: currentPerson?.mailing_postal_code,
				  }) && (
				 	<FormField
				 		name='kaiserArbitrationAgreement'
				 		label={<WysiwygWithReactLink className='rich-text--full' content={labelSettings?.kaiserArbitration} />}
				 		component='checkBox'
				 		touched={formProps.touched}
				 		errors={formProps.errors}
				 		value={formProps?.values?.kaiserArbitrationAgreement ?? false}
				 		checked={formProps?.values?.kaiserArbitrationAgreement ?? false}
				 	/>
				 )}
				</ContentSpacer>
			</div>
		</>
	);
}